/**
 * HomePage.tsx — Página inicial do site institucional Nectaris
 * Fiel ao Claude Design: Nectaris - Home.dc.html
 * Seções em ordem: Hero (SVG orbital), Marquees A+B, Seis Sintomas,
 * Serviços (SV/01-06 com HexField canvas), Cultura Data-Driven, Credibilidade, CTA+Footer
 *
 * ORB-SITE-002: animações via data-reveal / data-parallax / data-spin
 * gerenciadas pelo useNectarisFx em SiteLayout (sem hook local de reveal).
 */

import { NectarisLogoMark } from "../atoms";
import { HexField } from "../HexField";

/* ============================================================
   HERO — com SVG orbital animado + data-spin
   ============================================================ */
function Hero() {
  return (
    <header className="ns-hero" aria-labelledby="hero-heading">
      <div className="ns-hero__inner">
        {/* Coluna texto */}
        <div className="ns-hero__copy">
          <p className="ns-hero__eyebrow" aria-label="estrutura, tecnologia, execução">
            estrutura · tecnologia · execução
          </p>
          <h1 className="ns-hero__h1" id="hero-heading">
            Eficiência empresarial,{" "}
            <span className="ns-accent">de ponta a ponta.</span>
          </h1>
          <p className="ns-hero__lead">
            A Nectaris projeta e opera a engrenagem de gestão de empresas em crescimento.
            Mapeamos a operação como ela é, automatizamos o que consome as equipes e
            instalamos a cadência que transforma decisão em resultado no caixa.
          </p>
          <div className="ns-hero__ctas">
            <a href="/contato" className="ns-btn-primary">
              Agendar diagnóstico
            </a>
            <a href="#servicos" className="ns-btn-ghost">
              Explorar os serviços
            </a>
          </div>
        </div>

        {/* Coluna SVG orbital — data-spin acumula scroll+drift */}
        <div className="ns-hero__orbital" aria-hidden="true">
          <div
            className="ns-hero__orbital-wrap"
            data-spin="0.03"
            data-spin-drift="0.0015"
          >
            <svg
              viewBox="0 0 440 440"
              className="ns-hero__orbital-svg"
              aria-hidden="true"
            >
              {/* Anel externo pontilhado */}
              <circle
                cx="220" cy="220" r="204"
                fill="none"
                stroke="rgba(55,183,214,.16)"
                strokeWidth="1"
                strokeDasharray="2 7"
              />
              {/* Elipse orbital principal animada */}
              <ellipse
                cx="220" cy="220" rx="176" ry="64"
                fill="none"
                stroke="rgba(55,183,214,.42)"
                strokeWidth="1.3"
                transform="rotate(-24 220 220)"
                className="ns-animated-ellipse"
                strokeDasharray="10 14"
              />
              {/* Elipse estática */}
              <ellipse
                cx="220" cy="220" rx="176" ry="64"
                fill="none"
                stroke="rgba(55,183,214,.2)"
                strokeWidth="1"
                transform="rotate(66 220 220)"
              />
              {/* Elipse âmbar animada */}
              <ellipse
                cx="220" cy="220" rx="134" ry="48"
                fill="none"
                stroke="rgba(224,164,59,.5)"
                strokeWidth="1.3"
                transform="rotate(18 220 220)"
                className="ns-animated-ellipse-2"
                strokeDasharray="8 12"
              />
              {/* Círculo central glow */}
              <circle cx="220" cy="220" r="118" fill="rgba(55,183,214,.06)" />
              {/* Partícula ciano grande */}
              <circle r="3.4" fill="#78D6EB">
                <animateMotion
                  dur="10s"
                  repeatCount="indefinite"
                  path="M380.8 148.4 A176 64 -24 1 1 59.2 291.6 A176 64 -24 1 1 380.8 148.4"
                />
              </circle>
              {/* Partícula âmbar */}
              <circle r="3.8" fill="#E0A43B">
                <animateMotion
                  dur="7s"
                  repeatCount="indefinite"
                  begin="-2.5s"
                  path="M347.4 261.4 A134 48 18 1 1 92.6 178.6 A134 48 18 1 1 347.4 261.4"
                />
              </circle>
              {/* Partícula ciano pequena */}
              <circle r="2.2" fill="rgba(120,214,235,.6)">
                <animateMotion
                  dur="14s"
                  repeatCount="indefinite"
                  begin="-6s"
                  path="M59.2 291.6 A176 64 -24 1 1 380.8 148.4 A176 64 -24 1 1 59.2 291.6"
                />
              </circle>
            </svg>
            {/* Logo central — contra-rotação via data-spin-counter */}
            <div
              className="ns-hero__orbital-logo"
              aria-label="Nectaris"
              data-spin-counter
            >
              <NectarisLogoMark size={128} variant="dark" />
            </div>
          </div>
        </div>
      </div>

      {/* Grid de 4 stats */}
      <div className="ns-hero__stats">
        <div className="ns-hero__stats-grid" role="list" aria-label="Diferenciais do grupo">
          <div className="ns-hero__stat" role="listitem">
            <div className="ns-hero__stat-icon" aria-hidden="true">⚙</div>
            <div className="ns-hero__stat-title">Operamos sobre o que existe</div>
            <p className="ns-hero__stat-desc">ERP, CRM, bancos e planilhas entram como estão</p>
          </div>
          <div className="ns-hero__stat" role="listitem">
            <div className="ns-hero__stat-icon" aria-hidden="true">◉</div>
            <div className="ns-hero__stat-title">Todo número tem lastro</div>
            <p className="ns-hero__stat-desc">cada valor abre até o lançamento de origem</p>
          </div>
          <div className="ns-hero__stat" role="listitem">
            <div className="ns-hero__stat-icon" aria-hidden="true">⬡</div>
            <div className="ns-hero__stat-title">Decisão vira experimento</div>
            <p className="ns-hero__stat-desc">hipótese, meta e efeito medido no mês seguinte</p>
          </div>
          <div className="ns-hero__stat" role="listitem">
            <div className="ns-hero__stat-icon" aria-hidden="true">▲</div>
            <div className="ns-hero__stat-title">O ganho vira padrão</div>
            <p className="ns-hero__stat-desc">documentado, treinado e em uso depois do projeto</p>
          </div>
        </div>
      </div>
    </header>
  );
}

/* ============================================================
   MARQUEES
   ============================================================ */
const MARQUEE_A_ITEMS = [
  "ESTRUTURA", "TECNOLOGIA", "EXECUÇÃO", "MARGEM", "CAIXA PROJETADO",
  "DADO COM ORIGEM", "CADÊNCIA DE GESTÃO", "DECISÃO MEDIDA",
  "EFICIÊNCIA OPERACIONAL", "ACCOUNTABILITY", "PREVISIBILIDADE", "ESCALA COM CONTROLE",
];

const MARQUEE_B_ITEMS = [
  "CONSULTORIA OPERACIONAL", "DESENVOLVIMENTO DE SOFTWARE",
  "ESTRUTURAÇÃO DE OPERAÇÕES FINANCEIRAS", "GESTÃO DE PERFORMANCE",
  "OPERAÇÃO ASSISTIDA", "INVESTIMENTO DIRETO",
];

function Marquees() {
  return (
    <>
      {/* Marquee A — warm amber, animação para esquerda */}
      <div className="ns-marquee-a" aria-hidden="true">
        <div className="ns-marquee-a__track">
          {/* Dois blocos para scroll infinito suave */}
          <div className="ns-marquee-a__inner">
            {MARQUEE_A_ITEMS.map((item, i) => (
              <span key={`a1-${String(i)}`}>
                {item}
                <span className="ns-marquee-a__sep">◆</span>
              </span>
            ))}
          </div>
          <div className="ns-marquee-a__inner" aria-hidden="true">
            {MARQUEE_A_ITEMS.map((item, i) => (
              <span key={`a2-${String(i)}`}>
                {item}
                <span className="ns-marquee-a__sep">◆</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Marquee B — dark navy, animação para direita */}
      <div className="ns-marquee-b" aria-hidden="true">
        <div className="ns-marquee-b__track">
          <div className="ns-marquee-b__inner">
            {MARQUEE_B_ITEMS.map((item, i) => (
              <span key={`b1-${String(i)}`}>
                {item}
                <span className="ns-marquee-b__sep">/</span>
              </span>
            ))}
          </div>
          <div className="ns-marquee-b__inner" aria-hidden="true">
            {MARQUEE_B_ITEMS.map((item, i) => (
              <span key={`b2-${String(i)}`}>
                {item}
                <span className="ns-marquee-b__sep">/</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

/* ============================================================
   SEIS SINTOMAS
   ============================================================ */
const SINTOMAS = [
  {
    num: "01",
    title: "Decisão tomada no escuro",
    desc: "O dado existe em cinco sistemas e não fecha em nenhum. A liderança decide pelo que sente, não pelo que sabe.",
  },
  {
    num: "02",
    title: "Gente boa em trabalho de máquina",
    desc: "Conciliação, digitação e consolidação consomem as horas que deveriam ir para análise e cliente.",
  },
  {
    num: "03",
    title: "Caixa que surpreende",
    desc: "Sem projeção semanal confiável, o aperto chega junto com o vencimento e a negociação vira urgência.",
  },
  {
    num: "04",
    title: "Tecnologia contratada, não adotada",
    desc: "Sistemas e ferramentas de IA foram comprados, mas ninguém definiu processo, dono ou indicador. A licença roda; o ganho não aparece.",
  },
  {
    num: "05",
    title: "Resultado sem responsável",
    desc: "Quando a margem cai, cada área aponta para a outra. Sem dono por contrato, unidade ou processo, o desvio não vira ação.",
  },
  {
    num: "06",
    title: "Crescimento que não escala",
    desc: "A receita sobe e o custo operacional sobe junto. Mais volume, mesma margem: a estrutura consome o próprio crescimento.",
  },
];

function SeisSintomas() {
  return (
    <section
      className="ns-sintomas"
      aria-labelledby="sintomas-heading"
    >
      <div className="ns-wrap">
        <div className="ns-sintomas__header">
          <div>
            <p className="ns-eyebrow">o que resolvemos</p>
            <h2
              id="sintomas-heading"
              data-reveal
              data-reveal-delay="0"
              style={{ maxWidth: "22ch" }}
            >
              Seis sintomas de uma empresa que cresce sem margem
            </h2>
          </div>
          <p className="ns-sintomas__header-lead" data-reveal data-reveal-delay="100">
            Se dois destes aparecem na sua operação, o problema não é esforço. É estrutura.
          </p>
        </div>

        <div
          className="ns-sintomas__grid"
          data-reveal
          data-reveal-delay="150"
          role="list"
          aria-label="Seis sintomas de ineficiência"
        >
          {SINTOMAS.map((s) => (
            <article key={s.num} className="ns-sintoma-card" role="listitem">
              <span className="ns-sintoma-card__num" aria-hidden="true">{s.num}</span>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </article>
          ))}
        </div>

        {/* Box contexto de mercado */}
        <div
          className="ns-market-context"
          data-reveal
          data-reveal-delay="200"
          aria-label="Contexto do mercado"
        >
          <span className="ns-market-context__label">O CONTEXTO<br />DO MERCADO</span>
          <p className="ns-market-context__text">
            Empresas que decidem com base em dados são de{" "}
            <strong className="ns-strong">5% a 6% mais produtivas</strong>{" "}
            que as concorrentes, segundo estudo do MIT com 179 companhias. Do outro lado da
            conta, a Gartner estima em{" "}
            <strong className="ns-strong">US$ 12,9 milhões por ano</strong>{" "}
            o custo médio da má qualidade de dados numa empresa. A diferença entre os dois
            números é a janela de vantagem de quem estrutura primeiro.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   SERVIÇOS — SV/01 a SV/06
   ============================================================ */
const SV_02_05 = [
  {
    code: "SV/02 · DESENVOLVIMENTO DE SOFTWARE",
    title: "Quando a prateleira não resolve, construímos.",
    desc: "Integrações entre sistemas, plataformas internas e produtos completos que nascem dentro da operação e ficam com ela. O Orbita, software de gestão do grupo, nasceu assim.",
  },
  {
    code: "SV/03 · ESTRUTURAÇÃO DE OPERAÇÕES & VIABILIDADE ECONÔMICA",
    title: "A tese testada antes do investimento.",
    desc: "Nova unidade, novo negócio, nova linha: modelamos a economia da operação, desenhamos processos e entregamos o plano que separa a intenção do aporte.",
  },
  {
    code: "SV/04 · GESTÃO DE PERFORMANCE & DESEMPENHO",
    title: "A meta sai do quadro e entra na rotina.",
    desc: "Indicadores que fecham no prazo, metas que descem até cada área e um ritual de gestão em que o desvio aparece com dono, prazo e contramedida.",
  },
  {
    code: "SV/05 · OPERAÇÃO ASSISTIDA",
    title: "Conduzimos junto, até a régua ficar na casa.",
    desc: "Da agenda de fechamento à reunião de resultado, operamos a rotina lado a lado com o seu time e saímos quando a cadência se sustenta sozinha.",
  },
];

function Servicos() {
  return (
    <section
      id="servicos"
      className="ns-servicos"
      aria-labelledby="servicos-heading"
    >
      <div className="ns-wrap">
        <div className="ns-servicos__header">
          <div>
            <p className="ns-eyebrow">serviços</p>
            <h2
              id="servicos-heading"
              data-reveal
              style={{ maxWidth: "22ch" }}
            >
              Da leitura da operação à operação assistida
            </h2>
          </div>
          <p
            className="ns-servicos__header-lead"
            data-reveal
            data-reveal-delay="100"
          >
            Frentes que se contratam juntas ou separadas. Todas começam do mesmo ponto:
            enxergar a operação como ela é.
          </p>
        </div>

        {/* SV/01 — Card dark full-width com HexField canvas */}
        <div
          className="ns-sv01"
          data-reveal
          data-reveal-delay="120"
          aria-label="Serviço SV/01: Consultoria Operacional"
        >
          <div className="ns-sv01__copy">
            <p className="ns-sv01__eyebrow">SV/01 · CONSULTORIA OPERACIONAL</p>
            <h3 className="ns-sv01__h3">Antes de redesenhar, precisamos enxergar.</h3>
            <p className="ns-sv01__p">
              A Nectaris desenha o grafo real da operação e identifica os pontos que precisam
              de governança, automação ou cadência.
            </p>
            <p className="ns-sv01__p">
              Cada empresa que atendemos é mapeada como uma colmeia: núcleos de decisão,
              células operacionais, fluxos de informação e canais de feedback. O resultado é
              uma topologia viva, não uma planilha estática.
            </p>
          </div>
          {/* HexField canvas — fiel ao <hex-field size="34"> do original */}
          <div className="ns-sv01__hive" aria-hidden="true">
            <HexField size={34} style={{ width: "100%", height: "100%" }} />
          </div>
        </div>

        {/* SV/02-05 — Grid 2×2 */}
        <div
          className="ns-sv-grid"
          data-reveal
          data-reveal-delay="150"
          aria-label="Serviços SV/02 a SV/05"
        >
          {SV_02_05.map((svc) => (
            <article key={svc.code} className="ns-sv-card">
              <p className="ns-sv-card__eyebrow">{svc.code}</p>
              <h3>{svc.title}</h3>
              <p>{svc.desc}</p>
            </article>
          ))}
        </div>

        {/* SV/06 — Card dark institucional */}
        <div
          className="ns-sv06"
          data-reveal
          data-reveal-delay="180"
          aria-label="Serviço SV/06: Investimento Direto"
        >
          <div className="ns-sv06__copy">
            <p className="ns-sv06__eyebrow">SV/06 · INVESTIMENTO DIRETO</p>
            <h3 className="ns-sv06__h3">Quando a convicção é grande, entramos como sócios.</h3>
          </div>
          <p className="ns-sv06__p">
            Em teses selecionadas, o grupo participa do capital e assume a construção da
            operação ao lado do fundador: estrutura financeira, tecnologia e cadência de gestão
            desde o primeiro dia. É o formato mais exigente da nossa atuação, e o que melhor
            mostra no que acreditamos.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   CULTURA DATA-DRIVEN
   ============================================================ */
const CULTURA_CARDS = [
  {
    eyebrow: "01 · PERGUNTA",
    title: "Uma dúvida de dono",
    desc: '"por que a margem caiu nesta obra?" vira o ponto de partida, não o palpite mais alto da sala.',
    amber: false,
  },
  {
    eyebrow: "02 · EVIDÊNCIA",
    title: "Dado com etiqueta",
    desc: "origem, data e responsável em cada número, conferido contra a fonte antes de subir para a mesa.",
    amber: false,
  },
  {
    eyebrow: "03 · DECISÃO",
    title: "Dono, prazo e meta",
    desc: "a reunião termina com ação atribuída e o resultado esperado escrito, não com uma lista de intenções.",
    amber: false,
  },
  {
    eyebrow: "04 · APRENDIZADO",
    title: "O que funciona vira padrão",
    desc: "cada ciclo alimenta o próximo: a régua sobe e o ganho deixa de ser sorte.",
    amber: true,
  },
];

function CulturaDataDriven() {
  return (
    <section
      className="ns-cultura"
      aria-labelledby="cultura-heading"
    >
      <div className="ns-wrap">
        <div className="ns-cultura__live" aria-hidden="true">
          <span className="ns-cultura__live-dot" />
          <span className="ns-cultura__live-label">cultura data driven</span>
        </div>
        <h2 className="ns-cultura__h2" id="cultura-heading" data-reveal>
          Aqui, opinião é hipótese até virar número
        </h2>
        <p className="ns-cultura__lead" data-reveal data-reveal-delay="100">
          Instalamos na empresa o hábito de medir antes de discutir: toda decisão relevante
          nasce de um dado com origem, ganha uma meta e volta na reunião seguinte com o efeito
          no resultado. É assim que a eficiência para de depender de talento individual.
        </p>

        <div
          className="ns-cultura__grid"
          data-reveal
          data-reveal-delay="150"
          aria-label="Ciclo de cultura analítica"
        >
          {CULTURA_CARDS.map((card) => (
            <article
              key={card.eyebrow}
              className={`ns-cultura-card${card.amber ? " ns-cultura-card--amber" : ""}`}
            >
              <span className="ns-cultura-card__eyebrow">{card.eyebrow}</span>
              <strong className="ns-cultura-card__title">{card.title}</strong>
              <p className="ns-cultura-card__desc">{card.desc}</p>
            </article>
          ))}
        </div>

        <div
          className="ns-cultura__stats"
          data-reveal
          data-reveal-delay="200"
          aria-label="Estatísticas de referência"
        >
          <p className="ns-cultura__stat">
            <strong className="ns-strong">5% a 6% mais produtivas</strong>{" "}
            são as empresas que decidem com base em dados, em estudo do MIT com 179 companhias
          </p>
          <p className="ns-cultura__stat">
            <strong className="ns-strong">US$ 12,9 mi por ano</strong>{" "}
            é o custo médio da má qualidade de dados em uma empresa, segundo a Gartner
          </p>
          <p className="ns-cultura__stat">
            <strong className="ns-strong">82% das quebras</strong>{" "}
            de pequenas empresas envolvem má gestão de caixa, aponta estudo do U.S. Bank
          </p>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   CREDIBILIDADE
   ============================================================ */
const CRED_ITEMS = [
  {
    icon: "◎",
    title: "Auditável por construção",
    desc: "Todo total abre até o lançamento que o compõe e fecha centavo a centavo com a fonte.",
  },
  {
    icon: "🔒",
    title: "Sigilo como regra",
    desc: "Confidencialidade acordada por contrato, acesso segregado por empresa e por perfil.",
  },
  {
    icon: "≡",
    title: "Sem número inventado",
    desc: "Célula vazia fica vazia e toda limitação é declarada. Preferimos a resposta incômoda à estimativa confortável.",
  },
  {
    icon: "📋",
    title: "Método documentado",
    desc: "Cada entrega segue padrão versionado, o que evita dependência de pessoas e acelera o próximo ciclo.",
  },
  {
    icon: "▲",
    title: "Escola de grande porte",
    desc: "O rigor vem de controladorias de holdings e gestoras, adaptado ao tamanho da sua operação.",
  },
  {
    icon: "◷",
    title: "Compromisso com prazo",
    desc: "Escopo, entrega e resultado esperado combinados por escrito desde a proposta.",
  },
];

function Credibilidade() {
  return (
    <section
      className="ns-credibilidade"
      aria-labelledby="credibilidade-heading"
    >
      <div className="ns-wrap">
        <p className="ns-eyebrow">por que confiar no grupo</p>
        <h2 id="credibilidade-heading" data-reveal style={{ marginBottom: 0 }}>
          Credibilidade se prova no processo
        </h2>

        <div
          className="ns-credibilidade__grid"
          data-reveal
          data-reveal-delay="100"
          role="list"
          aria-label="Atributos de credibilidade"
        >
          {CRED_ITEMS.map((item) => (
            <article key={item.title} className="ns-cred-card" role="listitem">
              <div className="ns-cred-card__icon" aria-hidden="true">{item.icon}</div>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   CTA + FOOTER
   ============================================================ */
function CtaFooter() {
  return (
    <section className="ns-cta-section" aria-labelledby="cta-heading">
      <div className="ns-cta-section__inner">
        <h2 className="ns-cta-section__h2" id="cta-heading" data-reveal>
          Comece pelo diagnóstico.{" "}
          <span className="ns-accent-dark">O resultado aparece em dias.</span>
        </h2>
        <a href="/contato" className="ns-cta-section__btn" data-reveal data-reveal-delay="150">
          Falar com o grupo
        </a>
      </div>

      <footer className="ns-footer" aria-label="Rodapé Nectaris">
        <div className="ns-footer__inner">
          <a href="/" className="ns-footer__brand" aria-label="Nectaris — início">
            <NectarisLogoMark size={30} variant="dark" />
            <span className="ns-footer__wordmark">NECTARIS</span>
          </a>
          <span className="ns-footer__copy">© 2026 NECTARIS · BRASIL</span>
        </div>
      </footer>
    </section>
  );
}

/* ============================================================
   EXPORT — Página Home
   ============================================================ */
export function HomePage() {
  return (
    <>
      <Hero />
      <Marquees />
      <SeisSintomas />
      <Servicos />
      <CulturaDataDriven />
      <Credibilidade />
      <CtaFooter />
    </>
  );
}
